import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './Emails.css';

const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') return '/api';
  return 'http://localhost:8000/api';
};

const API = getApiUrl();

export default function Emails() {
  const { token } = useAuth();
  const [tab, setTab] = useState('inbox');
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [error, setError] = useState('');
  const [myEmail, setMyEmail] = useState('');

  const [showImages, setShowImages] = useState(false);

  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState('');

  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  const fetchInbox = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/mail/inbox`, { headers });
      const data = await res.json();
      if (data.success) {
        setMessages(data.messages || []);
        setMyEmail(data.email || '');
      } else {
        setError(data.message || 'Erreur chargement');
      }
    } catch {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { fetchInbox(); }, [fetchInbox]);

  const openMessage = async (msg) => {
    setSelected(msg.uid);
    setSelectedMsg(null);
    setLoadingMsg(true);
    setShowImages(false);
    try {
      const res = await fetch(`${API}/mail/message/${msg.uid}`, { headers });
      const data = await res.json();
      if (data.success) setSelectedMsg(data.message);
    } catch {}
    setLoadingMsg(false);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setSending(true);
    setSendResult('');
    try {
      const res = await fetch(`${API}/mail/send`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ to, subject, body }),
      });
      const data = await res.json();
      if (data.success) {
        setSendResult('success');
        setTo(''); setSubject(''); setBody('');
      } else {
        setSendResult(data.message || 'Erreur envoi');
      }
    } catch {
      setSendResult('Erreur de connexion');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="emails-page">
      <div className="emails-header">
        <div>
          <h1>📧 Messagerie</h1>
          {myEmail && <p className="emails-address">{myEmail}</p>}
        </div>
        <div className="emails-tabs">
          <button className={tab === 'inbox' ? 'tab active' : 'tab'} onClick={() => setTab('inbox')}>
            📥 Boîte de réception{messages.filter(m => !m.seen).length > 0 && <span className="badge">{messages.filter(m => !m.seen).length}</span>}
          </button>
          <button className={tab === 'compose' ? 'tab active' : 'tab'} onClick={() => setTab('compose')}>
            ✏️ Nouveau message
          </button>
        </div>
      </div>

      {tab === 'inbox' && (
        <div className="inbox-layout">
          <div className="message-list">
            <div className="list-header">
              <span>{messages.length} message{messages.length !== 1 ? 's' : ''}</span>
              <button className="btn-refresh" onClick={fetchInbox} disabled={loading}>🔄</button>
            </div>
            {loading && <div className="loading-msg">Chargement...</div>}
            {error && <div className="error-msg">{error}</div>}
            {!loading && messages.length === 0 && !error && <div className="empty-inbox">Aucun message</div>}
            {messages.map(msg => (
              <div
                key={msg.uid}
                className={`message-row ${!msg.seen ? 'unread' : ''} ${selected === msg.uid ? 'selected' : ''}`}
                onClick={() => openMessage(msg)}
              >
                <div className="msg-from">{msg.from || 'Expéditeur inconnu'}</div>
                <div className="msg-subject">{msg.subject || '(sans objet)'}</div>
                <div className="msg-date">{msg.date || ''}</div>
              </div>
            ))}
          </div>

          <div className="message-detail">
            {!selected && <div className="no-selection">Sélectionnez un message</div>}
            {loadingMsg && <div className="loading-msg">Chargement...</div>}
            {selected && selectedMsg && !loadingMsg && (
              <div className="message-content">
                <div className="message-header-detail">
                  <h2>{selectedMsg.parsed?.subject || '(sans objet)'}</h2>
                  <div className="msg-meta">
                    <span><strong>De :</strong> {selectedMsg.parsed?.from}</span>
                    <span><strong>Date :</strong> {selectedMsg.parsed?.date}</span>
                  </div>
                </div>
                {selectedMsg.parsed?.has_images && !showImages && (
                  <div className="images-banner">
                    Les images sont masquées.{' '}
                    <button className="btn-show-images" onClick={() => setShowImages(true)}>Afficher les images</button>
                  </div>
                )}
                <div
                  className={`message-body${showImages ? '' : ' hide-images'}`}
                  dangerouslySetInnerHTML={{ __html: selectedMsg.parsed?.body_html || '' }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'compose' && (
        <div className="compose-panel">
          <h2>Nouveau message</h2>
          {sendResult === 'success' && <div className="success-msg">✅ Message envoyé avec succès</div>}
          {sendResult && sendResult !== 'success' && <div className="error-msg">{sendResult}</div>}
          <form onSubmit={handleSend} className="compose-form">
            <div className="form-group">
              <label>À</label>
              <input type="email" value={to} onChange={e => setTo(e.target.value)} required placeholder="destinataire@exemple.com" />
            </div>
            <div className="form-group">
              <label>Objet</label>
              <input type="text" value={subject} onChange={e => setSubject(e.target.value)} required placeholder="Objet du message" />
            </div>
            <div className="form-group">
              <label>Message</label>
              <textarea value={body} onChange={e => setBody(e.target.value)} required rows={12} placeholder="Votre message..." />
            </div>
            <div className="compose-actions">
              <button type="submit" className="btn-send" disabled={sending}>
                {sending ? '⏳ Envoi...' : '📤 Envoyer'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
